import React from "react";
import {
  Box,
  Button,
  Stack,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, Cancel, Phone, Payment } from "@mui/icons-material";

/**
 * ActionButtons - Displays action buttons for quote interactions
 * Mobile-first responsive design with touch-friendly buttons
 */
export const ActionButtons = ({
  quote,
  tokenId,
  onAccept,
  onReject,
  onRequestCall,
  loading = false,
  termsAccepted = false,
}) => {
  const isQuoteActionable =
    quote?.status === "pending" ||
    quote?.status === "sent" ||
    quote?.status === "negotiating";
  const isQuoteAccepted = quote?.status === "accepted";
  const isQuoteRejected = quote?.status === "rejected";

  // Don't show action buttons if quote is rejected or expired
  if (isQuoteRejected || quote?.status === "expired") {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Stack spacing={2}>
        {/* Accept Quote Button */}
        {isQuoteActionable && (
          <Tooltip
            title={
              !termsAccepted ? "Please accept terms and conditions first" : ""
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CheckCircle />
                  )
                }
                onClick={onAccept}
                disabled={loading || !termsAccepted}
                sx={(theme) => ({
                  minHeight: 48,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  bgcolor: theme.palette.success.main,
                  "&:hover": {
                    bgcolor: theme.palette.success.dark,
                  },
                  "&:disabled": {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                })}
              >
                {loading ? "Processing..." : "Accept Quote"}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Payment Button - Show if quote is accepted */}
        {isQuoteAccepted && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Payment />}
            onClick={() => (window.location.href = `/quote/${tokenId}/payment`)}
            sx={{
              minHeight: 48,
              fontSize: "1.1rem",
              fontWeight: "bold",
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            View Payment Instructions
          </Button>
        )}

        {/* Secondary Actions */}
        {isQuoteActionable && (
          <Stack direction="row" spacing={1}>
            {/* Request Call Button */}
            <Button
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              onClick={onRequestCall}
              disabled={loading}
              sx={{
                flex: 1,
                minHeight: 48,
                fontSize: "1rem",
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  borderColor: "primary.dark",
                  bgcolor: "primary.50",
                },
              }}
            >
              Request Call
            </Button>

            {/* Reject Quote Button */}
            <Button
              variant="outlined"
              size="large"
              startIcon={<Cancel />}
              onClick={onReject}
              disabled={loading}
              sx={{
                flex: 1,
                minHeight: 48,
                fontSize: "1rem",
                borderColor: "error.main",
                color: "error.main",
                "&:hover": {
                  borderColor: "error.dark",
                  bgcolor: "error.50",
                },
              }}
            >
              Reject
            </Button>
          </Stack>
        )}

        {/* Status-specific messages */}
        {quote?.status === "pending_payment" && (
          <Box
            sx={{
              p: 2,
              bgcolor: "info.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "info.200",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Payment color="info" />
              <Typography variant="subtitle2" color="info.main">
                Payment Pending
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              We're verifying your payment. You'll receive a confirmation once
              it's processed.
            </Typography>
          </Box>
        )}

        {quote?.status === "completed" && (
          <Box
            sx={{
              p: 2,
              bgcolor: "success.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "success.200",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CheckCircle color="success" />
              <Typography variant="subtitle2" color="success.main">
                Order Completed
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Thank you for choosing Bubakii Transport! Your order has been
              completed successfully.
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
